import { useState, useEffect, useCallback } from "react";
import {
    Field,
    TextInput,
    Segmented,
    Actions,
    PrimaryButton,
    GhostButton,
    ResultBanner,
    BusyNote,
    ProgressBar,
    CardIntro,
} from "./ui/Form";
import api from "../api";
import { getTreatmentLayerId } from "../lib/treatmentLayer";

export default function SprayNotificationsCard() {
    const [selectedSubgrids, setSelectedSubgrids] = useState<string>("");
    const [scheduledNote, setScheduledNote] = useState<string>("");
    const [isFilling, setIsFilling] = useState<boolean>(false);
    const [bufferDistance, setBufferDistance] = useState<number>(300);
    const [bufferMode, setBufferMode] = useState<'envelope' | 'none'>('envelope');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isTesting, setIsTesting] = useState<boolean>(false);
    const [isGettingLayerInfo, setIsGettingLayerInfo] = useState<boolean>(false);
    // Removed debug resident sublayer functionality (isDebugging)
    const [result, setResult] = useState<any>(null);
    const [testResult, setTestResult] = useState<any>(null);
    const [layerInfo, setLayerInfo] = useState<any>(null);
    // Removed debugInfo state (no longer displaying debug resident sublayer panel)
    // Removed legacy buffer diagnostics state (bufferTestLoading, bufferTestResult)

    /** Pull the notification subgrids off this week's scheduled treatment areas.
     *
     *  This is the authoritative source, not the disease positives: an area may be sprayed with
     *  no positive in its subgrid, an adjacent subgrid may be chosen because the positive's own
     *  was sprayed last week, or the trigger may be a WNV-positive human case rather than a pool.
     *  Each area's GridLabels is geometry-derived, so an area straddling two subgrids yields both.
     */
    const fillFromScheduled = useCallback(async (quiet = false) => {
        setIsFilling(true);
        try {
            const res = await api.post("/list-treatment-areas", {
                layer_id: getTreatmentLayerId(),
                area_status: "Upcoming",
            });
            const areas: any[] = res.data?.areas || [];
            const found = Array.from(new Set(areas.flatMap((a) => a.gridlabels || []))).sort();
            if (!found.length) {
                if (!quiet) {
                    setScheduledNote(
                        areas.length
                            ? "Scheduled areas found, but none carry grid labels."
                            : "No scheduled treatment areas yet — build and schedule them in step 3 first."
                    );
                }
                return false;
            }
            setSelectedSubgrids(found.join(", "));
            setScheduledNote(
                `Filled ${found.length} subgrid${found.length === 1 ? "" : "s"} from ${areas.length} scheduled treatment area${areas.length === 1 ? "" : "s"}.`
            );
            return true;
        } catch (err: any) {
            if (!quiet) {
                setScheduledNote(err?.response?.data?.message || err.message || "Could not read the treatment layer.");
            }
            return false;
        } finally {
            setIsFilling(false);
        }
    }, []);

    /** Legacy path: subgrids handed over from the disease-positive analysis. Kept only as a
     *  fallback for when nothing is scheduled yet, and labelled as such — the positives are no
     *  longer the right source for who gets notified. */
    const fillFromPositives = useCallback((): boolean => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('auto_fill') !== 'true') return false;
        let filled: string | null = null;

        const sprayDataJson = localStorage.getItem('spray_notifications_prefill');
        if (sprayDataJson) {
            try {
                const sprayData = JSON.parse(sprayDataJson);
                // Ignore a stale handover (older than 10 minutes)
                if (Date.now() - sprayData.timestamp < 10 * 60 * 1000) filled = sprayData.subgrids;
            } catch (error) {
                console.error('Error parsing spray notifications data:', error);
            }
            localStorage.removeItem('spray_notifications_prefill');
        } else {
            const subgridsParam = urlParams.get('subgrids');
            if (subgridsParam) filled = decodeURIComponent(subgridsParam);
        }

        // Clean up the URL either way
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);

        if (!filled) return false;
        setSelectedSubgrids(filled);
        setScheduledNote(
            "Filled from the disease-positive analysis, not from scheduled areas — check these match what you actually scheduled."
        );
        return true;
    }, []);

    // Scheduled areas are the source of truth; only fall back to the positives handover when
    // nothing has been scheduled yet. One effect, so the two can't race to set the field.
    useEffect(() => {
        (async () => {
            const ok = await fillFromScheduled(true);
            if (!ok) fillFromPositives();
        })();
    }, [fillFromScheduled, fillFromPositives]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!selectedSubgrids.trim()) {
            setResult({ error: "Please enter subgrid numbers." });
            return;
        }

        setIsLoading(true);
        setResult(null);

        try {
            // Parse subgrid numbers from comma-separated string
            const subgridArray = selectedSubgrids
                .split(',')
                .map(s => s.trim())
                .filter(s => s.length > 0);

            // Validate GridLabel format (TRS-Quadrant, e.g., "172024-1")
            const gridLabelPattern = /^\d+-\d+$/;
            const invalidLabels = subgridArray.filter(sg => !gridLabelPattern.test(sg));
            
            if (invalidLabels.length > 0) {
                throw new Error(`Invalid GridLabel format: ${invalidLabels.join(', ')}. Expected format: 'TRS-Quadrant' (e.g., '172024-1')`);
            }

            const requestData = {
                selected_subgrids: subgridArray,
                buffer_distance: bufferDistance,
                buffer_mode: bufferMode
            };

            const response = await api.post("/generate-spray-notifications", requestData, {
                responseType: "blob", // Important for file download
            });

            // Create download link
            const blob = new Blob([response.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            
            // Generate filename with today's date
            const today = new Date().toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            }).replace(/\//g, '');
            link.download = `Spray_Notifications_${today}.csv`;
            
            link.click();
            window.URL.revokeObjectURL(url);

            const respBufferMode = (response as any)?.headers?.['x-buffer-mode'] || bufferMode;
            const respResidentCount = (response as any)?.headers?.['x-resident-count'];
            const respBufferUnits = (response as any)?.headers?.['x-buffer-units'];
            setResult({
                status: "success",
                message: "Spray notifications CSV generated and downloaded successfully!",
                subgrids_processed: subgridArray.length,
                buffer_distance: bufferDistance,
                buffer_mode: respBufferMode,
                resident_count: respResidentCount,
                buffer_units: respBufferUnits
            });

        } catch (err: any) {
            // The request asks for a blob so the CSV can be downloaded, which
            // means an error response arrives as a blob too -- and reading
            // .message off it gives undefined. That is where "Unknown error
            // occurred" came from while the server was returning a perfectly
            // clear explanation inside it. Unwrap the blob before giving up.
            let msg: string = err.message || "Unknown error occurred";
            const payload = err.response?.data;
            if (payload instanceof Blob) {
                try {
                    const parsed = JSON.parse(await payload.text());
                    if (parsed?.message) msg = parsed.message;
                } catch {
                    // Not JSON -- an HTML error page or an empty body. Leave
                    // the transport message, which at least carries the status.
                }
            } else if (payload?.message) {
                msg = payload.message;
            }
            setResult({ status: "failure", message: msg });
        } finally {
            setIsLoading(false);
        }
    };

    const testLayers = async () => {
        setIsTesting(true);
        setTestResult(null);

        try {
            const response = await api.post("/test-spray-layers", {});
            setTestResult(response.data);
        } catch (err: any) {
            const msg = err.response?.data ? 
                (err.response.data.message || "Test failed") : 
                err.message;
            setTestResult({ status: "failure", message: msg });
        } finally {
            setIsTesting(false);
        }
    };

    const getLayerInfo = async (layerId: string) => {
        setIsGettingLayerInfo(true);
        
        try {
            const response = await api.post("/get-layer-info", { layer_id: layerId });
            return response.data.layer_info;
        } catch (err: any) {
            console.error("Error getting layer info:", err);
            return null;
        } finally {
            setIsGettingLayerInfo(false);
        }
    };

    const showLayerInfo = async () => {
        const subgridInfo = await getLayerInfo("e8656893998e497fa8161f87d053a725");
        // Resident notices now comes from feature service item (sublayer 31). We fetch item-level info.
        const residentInfo = await getLayerInfo("49262609faee4515bad3b18b0b854e9b");
        
        setLayerInfo({
            subgrid: subgridInfo,
            resident: residentInfo
        });
    };

    // debugResidentNotices function removed (endpoint retained server-side for now, but UI hidden)

    // runBufferTest removed (deprecated backend endpoint)

    const handleSubgridChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Allow TRS-Quadrant format: numbers, hyphens, commas, and spaces
        const value = e.target.value.replace(/[^0-9,\s-]/g, '');
        setSelectedSubgrids(value);
    };

    // Parse subgrids for preview
    const subgridPreview = selectedSubgrids
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

    return (
                <div className="space-y-4">
            <CardIntro>
                Builds the CSV of residents to notify, from the subgrids you are treating.
                Send the result to Kathy.
            </CardIntro>

            <div>
                <div className="eyebrow mb-1.5">Buffer mode</div>
                <Segmented<'envelope' | 'none'>
                    value={bufferMode}
                    onChange={setBufferMode}
                    disabled={isLoading}
                    options={[
                        ['envelope', 'Buffered extents'],
                        ['none', 'Raw polygon'],
                    ]}
                />
                <p className="text-ink-faint text-[12px] mt-1.5">
                    {bufferMode === 'envelope'
                        ? `Catches residents within the subgrid plus a ${bufferDistance} ft buffer.`
                        : 'Only parcels intersecting the subgrid boundary itself — no buffer applied.'}
                </p>
            </div>

            {/* Test Section */}
                        <div className="rounded-sm2 border border-line-strong p-3 space-y-2">
                <div className="text-[13px] font-semibold">AGOL layers</div>
                <p className="text-ink-faint text-[12px]">
                    Check access before generating, so a permissions problem does not
                    surface part-way through.
                </p>
                <Actions>
                    <GhostButton
                        onClick={testLayers}
                        busy={isTesting}
                        busyLabel="Testing…"
                        disabled={isGettingLayerInfo || isLoading}
                    >
                        Test layer access
                    </GhostButton>
                    <GhostButton
                        onClick={showLayerInfo}
                        busy={isGettingLayerInfo}
                        busyLabel="Loading…"
                        disabled={isTesting || isLoading}
                    >
                        Get layer names
                    </GhostButton>
                </Actions>
            </div>

            {/* Layer Info Display */}
            {layerInfo && (
                <div className="mb-2 p-3 rounded-sm2 border border-line-strong">
                    <h3 className="font-semibold text-[13px] mb-2">Layer information</h3>
                    
                    {layerInfo.subgrid && (
                        <div className="mb-3">
                            <h4 className="eyebrow">Subgrid layer</h4>
                            <p className="text-[13px]">
                                <strong>{layerInfo.subgrid.title}</strong> ({layerInfo.subgrid.type})
                                <br />
                                <span className="text-[11px] text-ink-faint">
                                    Owner: {layerInfo.subgrid.owner} | Modified: {layerInfo.subgrid.modified}
                                </span>
                            </p>
                        </div>
                    )}
                    
                    {layerInfo.resident && (
                        <div>
                            <h4 className="eyebrow">Resident notices layer</h4>
                            <p className="text-[13px]">
                                <strong>{layerInfo.resident.title}</strong> ({layerInfo.resident.type})
                                <br />
                                <span className="text-[11px] text-ink-faint">
                                    Owner: {layerInfo.resident.owner} | Modified: {layerInfo.resident.modified}
                                </span>
                                <br />
                                <span className="text-[11px] text-ink-faint">
                                    Source Item ID: 49262609faee4515bad3b18b0b854e9b (target sublayer 31)
                                </span>
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Debug resident sublayer panel removed */}

            <form onSubmit={handleSubmit} className="space-y-4">
                                <Field
                    label="Subgrid grid labels"
                    hint="TRS-Quadrant, separated by commas"
                >
                    <TextInput
                        value={selectedSubgrids}
                        onChange={handleSubgridChange}
                        placeholder="e.g. 172024-1, 122136-1, 132201-2"
                        disabled={isLoading}
                        required
                    />
                </Field>

                <div className="flex items-center gap-2 flex-wrap -mt-1">
                    <GhostButton
                        type="button"
                        onClick={() => fillFromScheduled(false)}
                        busy={isFilling}
                        busyLabel="Loading…"
                        disabled={isLoading}
                    >
                        ⤓ Fill from scheduled treatment areas
                    </GhostButton>
                    {subgridPreview.length > 0 && (
                        <span className="text-ink-soft text-[13px]">
                            {subgridPreview.length} subgrid{subgridPreview.length !== 1 ? 's' : ''} selected
                        </span>
                    )}
                </div>
                {scheduledNote && (
                    <p className="text-ink-faint text-[12px] -mt-1">{scheduledNote}</p>
                )}
                {subgridPreview.length > 0 && (
                    <div className="rounded-sm2 bg-paper-2 p-2 text-[12px] font-mono break-words">
                        {subgridPreview.join(", ")}
                    </div>
                )}

                <Field
                    label="Buffer distance (feet)"
                    hint="How far around the treatment area to notify. Default 300 ft."
                >
                    <TextInput
                        type="number"
                        value={bufferDistance}
                        onChange={(e) => setBufferDistance(Number(e.target.value))}
                        min="1"
                        max="5000"
                        step="1"
                        disabled={isLoading || bufferMode === 'none'}
                        required
                    />
                </Field>

                <Actions>
                    <PrimaryButton
                        type="submit"
                        busy={isLoading}
                        busyLabel="Generating…"
                        disabled={!selectedSubgrids.trim()}
                    >
                        Generate notifications list
                    </PrimaryButton>
                </Actions>

                {isLoading && <ProgressBar />}
                {isLoading && (
                    <BusyNote>
                        Buffering the subgrids and intersecting resident parcels.
                    </BusyNote>
                )}
            </form>

            {result && (
                <div className="mt-4">
                    <h3 className="font-medium mb-2">Result:</h3>
                    <div className={`p-4 rounded text-sm ${
                        result.status === "success" 
                            ? "bg-brand-tint border border-line-strong" 
                            : "bg-red-50 border border-red-200"
                    }`}>
                        {result.status === "success" ? (
                            <div className="text-green-700">
                                <p className="font-medium">✅ {result.message}</p>
                                {result.subgrids_processed && (
                                    <p className="mt-1">
                                        Mode: <strong>{result.buffer_mode}</strong> | Subgrids: {result.subgrids_processed} | Buffer: {result.buffer_distance}ft {result.buffer_units ? `(${result.buffer_units})` : ''} {result.resident_count ? `| Residents: ${result.resident_count}` : ''}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="text-red-700">
                                <p className="font-medium">❌ Error</p>
                                <p className="mt-1">{result.message}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Test Results */}
            {testResult && (
                <div className="mt-4">
                    <h3 className="font-medium mb-2">Test Results:</h3>
                    <div className={`p-4 rounded text-sm max-h-96 overflow-y-auto ${
                        testResult.status === "success" 
                            ? "bg-paper-2 border border-line-strong" 
                            : "bg-red-50 border border-red-200"
                    }`}>
                        {testResult.status === "success" ? (
                            <div className="text-ink-soft">
                                <div className="space-y-3">
                                    {/* Subgrid Layer Test */}
                                    {testResult.tests?.subgrid_layer && (
                                        <div>
                                            <h4 className="font-medium">📍 Subgrid Layer:</h4>
                                            {testResult.tests.subgrid_layer.accessible ? (
                                                <div className="ml-4 text-sm">
                                                    <p>✅ <strong>Accessible</strong> - {testResult.tests.subgrid_layer.title}</p>
                                                    <p>📊 Features: {testResult.tests.subgrid_layer.feature_count}</p>
                                                    <p>🏷️ GridLabel field: {testResult.tests.subgrid_layer.has_gridlabel ? '✅ Found' : '❌ Missing'}</p>
                                                    {testResult.tests.subgrid_layer.sample_gridlabels?.length > 0 && (
                                                        <p>🔍 Sample GridLabels: {testResult.tests.subgrid_layer.sample_gridlabels.join(', ')}</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="ml-4 text-red-600">❌ Error: {testResult.tests.subgrid_layer.error}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Resident Notices Test */}
                                    {testResult.tests?.resident_notices_layer && (
                                        <div>
                                            <h4 className="font-medium">🏠 Resident Notices Layer:</h4>
                                            {testResult.tests.resident_notices_layer.accessible ? (
                                                <div className="ml-4 text-sm">
                                                    <p>✅ <strong>Accessible</strong> - {testResult.tests.resident_notices_layer.title}</p>
                                                    <p>📊 Features: {testResult.tests.resident_notices_layer.feature_count}</p>
                                                    <p>🔢 Sublayer: {testResult.tests.resident_notices_layer.sublayer_index}</p>
                                                </div>
                                            ) : (
                                                <p className="ml-4 text-red-600">❌ Error: {testResult.tests.resident_notices_layer.error}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* GridLabel Query Test */}
                                    {testResult.tests?.gridlabel_query && (
                                        <div>
                                            <h4 className="font-medium">🔍 GridLabel Query Test:</h4>
                                            {testResult.tests.gridlabel_query.success ? (
                                                <div className="ml-4 text-sm">
                                                    <p>✅ Query successful for: {testResult.tests.gridlabel_query.test_gridlabel}</p>
                                                    <p>🎯 Features found: {testResult.tests.gridlabel_query.features_found}</p>
                                                    <p>🗺️ Has geometry: {testResult.tests.gridlabel_query.has_geometry ? '✅' : '❌'}</p>
                                                </div>
                                            ) : (
                                                <p className="ml-4 text-red-600">❌ Error: {testResult.tests.gridlabel_query.error}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-red-700">
                                <p className="font-medium">❌ Test Failed</p>
                                <p className="mt-1">{testResult.message}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Information Panel */}
            <div className="mt-2 p-3 rounded-sm2 border border-line-strong">
                <h3 className="font-semibold text-[13px] mb-1">How this works</h3>
                <div className="text-[13px] text-ink-soft space-y-0.5">
                    <div>• <strong>Data Source:</strong> Live AGOL hosted layers</div>
                    <div>• <strong>Processing:</strong> Subgrids → Buffer → Intersect → Deduplicate</div>
                    <div>• <strong>Output:</strong> CSV file with resident notification details</div>
                    <div>• <strong>Next Step:</strong> Send generated CSV to Kathy for notifications</div>
                </div>
            </div>
        </div>
    );
}
